import { createTheme } from '@mui/material/styles';

const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#1A237E', // Deep Indigo
            light: '#534bae',
            dark: '#000051',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#009688', // Teal
            light: '#52c7b8',
            dark: '#00675b',
            contrastText: '#ffffff',
        },
        success: {
            main: '#2e7d32',
            light: '#4caf50',
        },
        warning: {
            main: '#ed6c02',
            light: '#ff9800',
        },
        error: {
            main: '#d32f2f',
            light: '#ef5350',
        },
        background: {
            default: '#F5F7FA',
            paper: '#ffffff',
        },
        text: {
            primary: '#121212',
            secondary: '#546e7a',
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
            fontWeight: 700,
            fontSize: '2.5rem',
            letterSpacing: '-0.02em',
        },
        h2: {
            fontWeight: 600,
            fontSize: '2rem',
            letterSpacing: '-0.01em',
        },
        h3: {
            fontWeight: 600,
            fontSize: '1.75rem',
            letterSpacing: '-0.01em',
        },
        h4: {
            fontWeight: 600,
            fontSize: '1.5rem',
        },
        h5: {
            fontWeight: 500,
            fontSize: '1.25rem',
        },
        h6: {
            fontWeight: 500,
            fontSize: '1rem',
        },
        button: {
            textTransform: 'none',
            fontWeight: 600,
        },
        subtitle2: {
            fontWeight: 600,
            fontSize: '0.8125rem',
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
        },
    },
    shape: {
        borderRadius: 8,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    boxShadow: 'none',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
                        transform: 'translateY(-1px)',
                    },
                    '&:active': {
                        transform: 'translateY(0)',
                    },
                },
                containedPrimary: {
                    background: 'linear-gradient(135deg, #1A237E 0%, #3F51B5 100%)',
                },
                outlinedPrimary: {
                    borderWidth: '1.5px',
                    '&:hover': {
                        borderWidth: '1.5px',
                        backgroundColor: 'rgba(26, 35, 126, 0.04)',
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
                    borderRadius: '12px',
                    transition: 'box-shadow 0.3s ease, transform 0.2s ease',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    transition: 'box-shadow 0.3s ease',
                },
                elevation3: {
                    boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.06)',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        transition: 'box-shadow 0.2s ease',
                        '&.Mui-focused': {
                            boxShadow: '0 0 0 3px rgba(26, 35, 126, 0.1)',
                        },
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    transition: 'box-shadow 0.2s ease',
                    '&.Mui-focused': {
                        boxShadow: '0 0 0 3px rgba(26, 35, 126, 0.1)',
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                },
                outlined: {
                    borderWidth: '1.5px',
                },
            },
        },
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    backdropFilter: 'blur(4px)',
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#1A237E',
                    color: '#ffffff',
                },
            },
        },
        MuiDataGrid: {
            styleOverrides: {
                root: {
                    border: 'none',
                    borderRadius: '12px',
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#f0f2f5',
                        borderBottom: '2px solid #e0e0e0',
                        borderRadius: '12px 12px 0 0',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 700,
                        fontSize: '0.8125rem',
                        color: '#1A237E',
                        letterSpacing: '0.02em',
                    },
                    '& .MuiDataGrid-cell': {
                        borderBottom: '1px solid #f0f0f0',
                        fontSize: '0.875rem',
                    },
                    '& .MuiDataGrid-row:hover': {
                        backgroundColor: 'rgba(26, 35, 126, 0.02)',
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                },
            },
        },
        MuiSnackbar: {
            defaultProps: {
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
            },
        },
    },
});

export default theme;
